// Formatting for dates and devices, in the person's locale.

const RELATIVE = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
const DATE_TIME = new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
const DATE = new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium' })

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
]

/** "3 hours ago", "in 6 days", "now". */
export const formatRelative = (iso: string, now = Date.now()): string => {
  const seconds = Math.round((new Date(iso).getTime() - now) / 1000)
  for (const [unit, size] of UNITS) {
    if (Math.abs(seconds) >= size) return RELATIVE.format(Math.round(seconds / size), unit)
  }
  return 'now'
}

export const formatDateTime = (iso: string): string => DATE_TIME.format(new Date(iso))

export const formatDate = (iso: string): string => DATE.format(new Date(iso))

const RUPEES = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' })

/** Paise as rupees, Indian grouping: 118000 → "₹1,180.00", -950 → "-₹9.50". */
export const formatRupees = (paise: number): string => RUPEES.format(paise / 100)

/** An amount in a currency's minor units, in that currency: (149900, 'INR') → "₹1,499.00". */
export const formatMoney = (minor: number, currency: string): string => {
  try {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency }).format(minor / 100)
  } catch {
    // A code Intl doesn't know: the number with the code.
    return `${(minor / 100).toFixed(2)} ${currency}`
  }
}

/** "Chrome on macOS" from a user agent string; good enough to recognise your own devices. */
export const describeDevice = (userAgent: string | null): string => {
  if (!userAgent) return 'Unknown device'
  const browser =
    [
      ['Edg/', 'Edge'],
      ['Firefox/', 'Firefox'],
      ['Chrome/', 'Chrome'],
      ['Safari/', 'Safari'],
    ].find(([marker]) => userAgent.includes(marker ?? ''))?.[1] ?? 'Browser'
  const system =
    [
      ['iPhone', 'iPhone'],
      ['Android', 'Android'],
      ['Mac OS X', 'macOS'],
      ['Windows', 'Windows'],
      ['Linux', 'Linux'],
    ].find(([marker]) => userAgent.includes(marker ?? ''))?.[1] ?? 'unknown system'
  return `${browser} on ${system}`
}

/** "AR" from "Asha Rao"; the first letter of the email when there is no name. */
export const initials = (name: string, email = ''): string => {
  const letters = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join('')
  return letters || email.slice(0, 1).toUpperCase() || '?'
}

/** "Address changed" from a key like "address_changed": for outcomes and answer keys with no label of their own. */
export const keyLabel = (key: string): string => {
  const words = key.replaceAll('_', ' ').trim()
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}`
}
